import {
	AssignmentFranchiseTypeEnum,
	DocumentObjectListRefTableEnum,
	TextMessage,
} from '@/constant';
import { removeNilOrEmptyObjDeep, showToast } from '@/utils';
import { PayloadAction } from '@reduxjs/toolkit';
import { put } from 'redux-saga/effects';

import * as API from 'services';

import { AxiosResponse } from 'axios';

import { IPayloadResetPasswordRequest } from '../foreign-applicant';
import { WorkerApplicantActions } from '../worker-applicant';
import { WorkerFranchiseActions } from '../worker-franchise';
import { WorkerLodgingActions } from '../worker-lodging';
import { WorkerProgramActions } from '../worker-program';
import { WorkerRecommenderActions } from '../worker-recommender';
import {
	formatAppUserInfo,
	formatColumnNameResponse,
	formatCommonApplicantAssignmentList,
	formatCommonApplicantList,
	formatCommonGetFields,
	formatCommonList,
	formatDocumentMappings,
	formatDocumentObjectList,
	formatSelectMasterList,
	formatUpdateFieldsRequest,
	formatUpdateHistoryList,
	formatUploadRequest,
} from './common.helper';
import { CommonActions } from './common.redux';
import {
	ICommonPayloadUpdateRecommenderRequest,
	IPayloadCreateCommonAssignmentRequest,
	IPayloadCreateCommonPublicAssignmentRequest,
	IPayloadGetAppUserInfoRequest,
	IPayloadGetCommonApplicantAssignmentListRequest,
	IPayloadGetCommonApplicantListRequest,
	IPayloadGetCommonListRequest,
	IPayloadGetDocumentJsonContentRequest,
	IPayloadGetDocumentMappingsRequest,
	IPayloadGetDocumentObjectDetailRequest,
	IPayloadGetDocumentObjectRequest,
	IPayloadGetUpdateHistoryListRequest,
	IPayloadGetYearRegisterRequest,
	IPayloadUpdateFieldsRequest,
	IPayloadUploadFileRequest,
	IPayloadVerifyDomainRequest,
	IPayloadVerifyEmailRequest,
	TCommonGetFieldRequest,
	TPayloadGetSettingColumnRequest,
	TPayloadGetSettingColumnSuccess,
	TPayloadSetSettingColumnRequest,
} from './common.type';

export function* verifyEmail(
	action: PayloadAction<IPayloadVerifyEmailRequest>
) {
	const { callback, email } = action.payload;

	try {
		const res: AxiosResponse = yield API.verifyEmail(email);

		yield put(CommonActions.verifyEmailSuccess());
		callback?.({ data: res?.data?.data });
	} catch (e: any) {
		callback?.({ error: e });
		yield put(CommonActions.verifyEmailFailure());
	}
}

export function* verifyDomain(
	action: PayloadAction<IPayloadVerifyDomainRequest>
) {
	const { callback, domain } = action.payload;

	try {
		const res: AxiosResponse = yield API.verifySubDomain(domain);

		yield put(CommonActions.verifyDomainSuccess());
		showToast(TextMessage.AVAILABLE_DOMAIN);
		callback?.({ data: res?.data?.data });
	} catch (e: any) {
		callback?.({ error: e });
		showToast(TextMessage.DUPLICATE_EMAIL_DOMAIN, { type: 'ERROR' });
		yield put(CommonActions.verifyDomainFailure());
	}
}

export function* getListCommonSaga(
	action: PayloadAction<IPayloadGetCommonListRequest>
) {
	const { type, callback, params } = action.payload;
	try {
		const res: API.ApiResponseSuccess<API.TGetCommonListResponse> =
			yield API.getCommonList(type, params);
		yield put(
			CommonActions.selectMasterInfoSuccess({
				type,
				data: formatSelectMasterList(res.data, type),
			})
		);
		yield put(
			CommonActions.getCommonListSuccess({
				type,
				data: formatCommonList(res.data, type),
				total: res?.total as any,
			})
		);
		callback?.({ data: formatCommonList(res.data, type) });
	} catch (e: any) {
		callback?.({ error: e });
		yield put(CommonActions.selectMasterInfoFailure({ type }));
		yield put(CommonActions.getCommonListFailure({ type }));
	}
}

export function* uploadFile(action: PayloadAction<IPayloadUploadFileRequest>) {
	const { data, callback } = action.payload;

	const formData = formatUploadRequest(data);

	try {
		const res: AxiosResponse = yield API.uploadFile(formData);
		callback?.({ data: res?.data });
	} catch (e: any) {
		callback?.({ error: e });
	}
}

export function* commonFileUpload(
	action: PayloadAction<IPayloadUploadFileRequest>
) {
	const { data, callback } = action.payload;

	const formData = formatUploadRequest(data);

	try {
		const res: AxiosResponse = yield API.commonFileUpload(formData);
		callback?.({ data: res?.data });
	} catch (e: any) {
		callback?.({ error: e });
	}
}

export function* updateFields(
	action: PayloadAction<IPayloadUpdateFieldsRequest>
) {
	const { data, callback, screenCode } = action.payload;

	const formData = formatUpdateFieldsRequest(data);

	try {
		const res: AxiosResponse<API.TUpdateFieldsResponse> =
			yield API.updateFields(formData, screenCode);
		callback?.({ data: data.data, fields: res?.data?.fields });
	} catch (e: any) {
		callback?.({ error: e });
	}
}

export function* getYearRegisterListSaga(
	action: PayloadAction<IPayloadGetYearRegisterRequest>
) {
	const { screenCode, callback } = action.payload;

	try {
		const res: API.ApiResponseSuccess<number[]> =
			yield API.getYearRegisterList(screenCode);

		yield put(
			CommonActions.getYearRegisterListSuccess({
				screenCode,
				data: res.data,
			})
		);
		callback?.({ data: res?.data });
	} catch (e: any) {
		callback?.({ error: e });
		yield put(CommonActions.getYearRegisterListFailure({ screenCode }));
	}
}

export function* getCommonDocumentObjectList(
	action: PayloadAction<IPayloadGetDocumentObjectRequest>
) {
	const { callback, ...params } = action.payload;
	try {
		const res: API.ApiResponseSuccess<API.GetCommonDocumentObjectListResponse> =
			yield API.getDocumentObjectList(params);

		yield put(
			CommonActions.getDocumentObjectListSuccess({
				refTableName: params.refTableName,
				data: formatDocumentObjectList(res.data, params.refTableName),
			})
		);
		callback?.({ data: res?.data });
	} catch (e: any) {
		callback?.({ error: e });
		yield put(
			CommonActions.getDocumentObjectListFailure({
				refTableName: params.refTableName,
			})
		);
	}
}

export function* getCommonDocumentObjectDetail(
	action: PayloadAction<IPayloadGetDocumentObjectDetailRequest>
) {
	const { callback, refTableName, data } = action.payload;

	if (refTableName === DocumentObjectListRefTableEnum.APPLICANT) {
		yield put(
			WorkerApplicantActions.getWorkerApplicantDetailSuccess({
				workerApplicantDetail: data,
			})
		);
	}
	if (refTableName === DocumentObjectListRefTableEnum.FRANCHISE) {
		yield put(
			WorkerFranchiseActions.getWorkerFranchiseDetailSuccess({
				workerFranchiseDetail: data,
			})
		);
	}
	if (refTableName === DocumentObjectListRefTableEnum.RECOMMENDER) {
		yield put(
			WorkerRecommenderActions.getWorkerRecommenderDetailSuccess({
				workerRecommenderDetail: data,
			})
		);
	}
	if (refTableName === DocumentObjectListRefTableEnum.PROGRAM) {
		yield put(
			WorkerProgramActions.getWorkerProgramDetailSuccess({
				workerProgramDetail: data,
			})
		);
	}
	if (refTableName === DocumentObjectListRefTableEnum.LODGING) {
		yield put(
			WorkerLodgingActions.getWorkerLodgingDetailSuccess({
				workerLodgingDetail: data,
			})
		);
	}

	callback?.();
}

export function* getSettingColumnSaga(
	action: PayloadAction<TPayloadGetSettingColumnRequest>
) {
	const { screenCode, callback } = action.payload;

	try {
		const res: API.ApiResponseSuccess<
			TPayloadGetSettingColumnSuccess['content']
		> = yield API.getUserSettingsListFields({ screenCode });

		const content = formatColumnNameResponse(res.data);

		yield put(
			CommonActions.getSettingColumnSuccess({
				screenCode,
				content,
			})
		);
		callback?.({ data: res?.data });
	} catch (e: any) {
		callback?.({ error: e });
		yield put(CommonActions.getSettingColumnFailure({ screenCode }));
	}
}

export function* setSettingColumnSaga(
	action: PayloadAction<TPayloadSetSettingColumnRequest>
) {
	const { callback, ...rest } = action.payload;

	try {
		const res: AxiosResponse = yield API.setUserSettings({ ...rest });
		callback?.({ data: res?.data });
	} catch (e: any) {
		callback?.({ error: e });
	}
}

export function* getUpdateHistoryListSaga(
	action: PayloadAction<IPayloadGetUpdateHistoryListRequest>
) {
	const { callback, ...params } = action.payload;
	try {
		const res: API.ApiResponseSuccess<API.GetUpdateHistoryListResponse> =
			yield API.getUpdateHistoryList(params);

		const content = formatUpdateHistoryList(res?.data?.content, {
			total: res.data?.total || 0,
			page: params.page || 1,
			pageSize: params.pageSize,
		});

		yield put(
			CommonActions.getUpdateHistoryListSuccess({
				...params,
				content,
				total: res.data?.total || 0,
			})
		);
		callback?.({ data: res?.data });
	} catch (e: any) {
		callback?.({ error: e });
		yield put(CommonActions.getUpdateHistoryListFailure());
	}
}

export function* getAutocompleteSuggestion() {
	try {
		const res: API.ApiResponseSuccess<API.TGetAutocompleteSuggestionResponse> =
			yield API.getAutocompleteSuggestion();

		yield put(
			CommonActions.getAutocompleteSuggestionSuccess({
				data: res?.data,
			})
		);
	} catch (e: any) {
		yield put(CommonActions.getAutocompleteSuggestionFailure());
	}
}

export function* getCommonApplicantListSaga(
	action: PayloadAction<IPayloadGetCommonApplicantListRequest>
) {
	const { type, callback, params } = action.payload;

	const isInvalidParams =
		params.type === AssignmentFranchiseTypeEnum.PUBLIC &&
		(!params.publicFranchiseId ||
			!params.franchiseId ||
			!params.startDate ||
			!params.endDate);

	if (isInvalidParams) {
		yield put(CommonActions.getCommonApplicantListFailure());
		return;
	}

	try {
		const newParams = removeNilOrEmptyObjDeep(params);

		const res: API.ApiResponseSuccess<API.GetCommonApplicantListResponse> =
			yield API.getCommonList(type, newParams);

		const applicantList = formatCommonApplicantList(res?.data?.content);

		yield put(
			CommonActions.getCommonApplicantListSuccess({
				data: applicantList,
				total: res.data?.total || 0,
			})
		);

		callback?.({ data: applicantList });
	} catch (e: any) {
		callback?.({ error: e });
		yield put(CommonActions.getCommonApplicantListFailure());
	}
}

export function* getCommonApplicantWithIdsSaga(
	action: PayloadAction<IPayloadGetCommonApplicantListRequest>
) {
	const { type, callback, params } = action.payload;
	try {
		const res: API.ApiResponseSuccess<API.GetCommonApplicantListResponse> =
			yield API.getCommonList(type, params);

		const applicantList = formatCommonApplicantList(res?.data?.content);

		yield put(CommonActions.getCommonApplicantWithIdsSuccess());

		callback?.({ data: applicantList });
	} catch (e: any) {
		callback?.({ error: e });
		yield put(CommonActions.getCommonApplicantWithIdsFailure());
	}
}

export function* getCommonApplicantAssignmentListSaga(
	action: PayloadAction<IPayloadGetCommonApplicantAssignmentListRequest>
) {
	const { callback, ...rest } = action.payload;
	try {
		const res: API.ApiResponseSuccess<API.GetCommonAssignmentApplicantListResponse> =
			yield API.getWorkerAssignmentApplicantList({ ...rest });

		const applicantList = formatCommonApplicantAssignmentList(
			res?.data?.content
		);

		yield put(
			CommonActions.getCommonApplicantAssignmentListSuccess({
				data: applicantList,
			})
		);

		callback?.({ data: applicantList });
	} catch (e: any) {
		callback?.({ error: e });
		yield put(CommonActions.getCommonApplicantAssignmentListFailure());
	}
}

export function* createCommonAssignment(
	action: PayloadAction<IPayloadCreateCommonAssignmentRequest>
) {
	const { callback, data } = action.payload;

	try {
		yield API.createCommonAssignment(data);

		yield put(CommonActions.createCommonAssignmentSuccess());
		callback?.();
	} catch (error: unknown) {
		callback?.({ error: error as API.ApiResponseError });
		yield put(CommonActions.createCommonAssignmentFailure());
	}
}

export function* createCommonPublicAssignment(
	action: PayloadAction<IPayloadCreateCommonPublicAssignmentRequest>
) {
	const { callback, data, deleteIds } = action.payload;

	try {
		yield API.createCommonPublicAssignment(data, deleteIds);

		yield put(CommonActions.createCommonPublicAssignmentSuccess());
		callback?.();
	} catch (error: unknown) {
		callback?.({ error: error as API.ApiResponseError });
		yield put(CommonActions.createCommonPublicAssignmentFailure());
	}
}

export function* updateCommonRecommenderSaga(
	action: PayloadAction<ICommonPayloadUpdateRecommenderRequest>
) {
	const { callback, data } = action.payload;

	try {
		yield API.updateCommonRecommender(data);

		yield put(CommonActions.updateCommonRecommenderSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(CommonActions.updateCommonRecommenderFailure());
	}
}

export function* getFieldsSaga(action: PayloadAction<TCommonGetFieldRequest>) {
	const { callback, ...data } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.TCommonGetFieldsResponse> =
			yield API.getFields(data);

		const content = formatCommonGetFields(res?.data);

		yield put(CommonActions.getFieldsSuccess());
		callback?.({ data: content });
	} catch (error) {
		callback?.({ error });
		yield put(CommonActions.getFieldsFailure());
	}
}

export function* getDocumentJsonContentSaga(
	action: PayloadAction<IPayloadGetDocumentJsonContentRequest>
) {
	const { callback, id } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.TGetDocumentDetailResponse> =
			yield API.getDocumentDetail(id);

		yield put(
			CommonActions.getDocumentJsonContentSuccess(res.data.jsonContent)
		);
		callback?.({ data: res.data.jsonContent });
	} catch (error) {
		callback?.({ error });
		yield put(CommonActions.getDocumentJsonContentFailure());
	}
}

export function* resetPasswordCommonSaga(
	action: PayloadAction<IPayloadResetPasswordRequest>
) {
	const { callback, id } = action.payload;
	try {
		yield API.resetDefaultPassword(id);

		yield put(CommonActions.resetPasswordSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(CommonActions.resetPasswordFailure());
	}
}

export function* unWithdrawnCommonSaga(
	action: PayloadAction<IPayloadResetPasswordRequest>
) {
	const { callback, id } = action.payload;
	try {
		yield API.unWithdrawn(id);

		yield put(CommonActions.unWithdrawnSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(CommonActions.unWithdrawnFailure());
	}
}

export function* getAppUserInfoSaga(
	action: PayloadAction<IPayloadGetAppUserInfoRequest>
) {
	const { id, type, callback } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetAppUserInfoResponse> =
			yield API.getAppUserInfo({ id, type });

		const appUserInfo = formatAppUserInfo(res.data);

		yield put(
			CommonActions.getAppUserInfoSuccess({
				appUserInfo,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(CommonActions.getAppUserInfoFailure());
	}
}

export function* getDocumentMappingsSaga(
	action: PayloadAction<IPayloadGetDocumentMappingsRequest>
) {
	const { callback, ...rest } = action.payload;
	try {
		const res: API.ApiResponseSuccess<API.GetDocumentMappingsResponse> =
			yield API.getDocumentMappings(rest);

		const data = formatDocumentMappings(res.data);

		yield put(CommonActions.getDocumentMappingsSuccess({ data }));
		callback?.(undefined, data);
	} catch (error) {
		callback?.(error, undefined);
		yield put(CommonActions.getDocumentMappingsFailure());
	}
}
